import { Component, OnDestroy } from '@angular/core';
import { BaseComponent } from '../../shared/base/base.component';
import * as L from 'leaflet';
import { BaseService } from '../../../providers/base.service';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { IssueFormComponent } from '../../home/issue-form/issue-form.component';
import { LocateControl } from "leaflet.locatecontrol";
import { addCenterControl } from '../map-center-control';
import { addSearchControl } from '../map-search-control';

// Wireless counterpart of the fiber map: the pin stays on the map centre, and
// for that point and a floor count the API says whether one of our POP sites is
// in line of sight (api/wireless_check). A feasible point opens a wireless lead,
// anything else a wireless inquiry.
// Anyone but staff (the public wireless-map page needs no login) gets the score
// only: no POP sites, no coverage layer, no towers. Their request is always an
// inquiry, which the API files as `system`, scores itself, and turns into a
// lead when that score is 70 or more.
@Component({
  selector: 'app-wirelessmap',
  templateUrl: './wirelessmap.component.html',
  styleUrls: ['./wirelessmap.component.scss']
})
export class WirelessmapComponent extends BaseComponent implements OnDestroy {
  map: L.Map;
  marker: L.Marker;
  beam: L.Polyline;
  floors: number; // empty until the first check answers with the server's default
  result: any;
  coverage: any; // the coverage map's floors, date and score bands, for the legend
  error: string;
  isPublic: boolean; // the route outside the layout, so the map takes the whole window

  get staff(): boolean {
    return !!this.token && (this.role || '').split(',').includes('PERSONNEL');
  }

  constructor(
    public service: BaseService,
    public translator: TranslateService,
    public cookieService: CookieService,
    public titleService: Title,
    public activeModal: NgbModal,
    public route: ActivatedRoute,) {
    super(service, translator, cookieService, titleService);
    this.isPublic = !!this.route.snapshot.data.public;
  }

  async ngOnInit() {
    await super.ngOnInit();
    if (this.isPublic) {
      this.translator.get('WIRELESS_MAP').subscribe(title => this.pageTitle = title);
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.checkTimer);
    this.map = null;
    this.marker = null;
  }

  mapOptions = {
    layers: [L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; Petiak System'
    })],
    zoom: 16,
    center: L.latLng([this.latitude || 35.77880664304775, this.longitude || 51.43510222434998])
  };

  async onMapReady(map: L.Map) {
    map.invalidateSize();
    this.map = map;
    // On the public page nothing has loaded the translations yet, and the
    // controls below take their texts with instant()
    await this.translator.get('SEARCH').toPromise();
    if (!this.map) {
      return; // destroyed meanwhile
    }

    new LocateControl({
      position: 'bottomleft',
      keepCurrentZoomLevel: true,
      strings: {
        title: this.translator.instant('MAP_LOCATOR_TITLE')
      }
    }).addTo(this.map);

    addSearchControl(map, this.translator, this.service);

    // Latitude and longitude boxes, to centre the map on a typed point
    addCenterControl(map, this.translator);

    // Create a marker and add it to the map's center
    this.marker = L.marker(this.map.getCenter(), {
      draggable: false,
      icon: L.icon({
        iconUrl: `assets/leaflet/marker-icon.png`,
        iconRetinaUrl: `assets/leaflet/marker-icon-2x.png`,
        shadowUrl: `assets/leaflet/marker-shadow.png`,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [0, -40]
      })
    }).addTo(this.map);

    // The line from the pin to the best POP site
    this.beam = L.polyline([], { color: 'green', dashArray: '6' }).addTo(this.map);

    this.map.on('move', () => {
      this.marker.setLatLng(this.map.getCenter()); // Keep the marker on the map's center
    });

    this.map.on('moveend', () => {
      this.scheduleCheck();
    });

    this.scheduleCheck();
    if (this.staff) {
      this.loadCoverage();
      this.loadPopSites();
    }
  }

  // The coverage map: where a building of the default floor count scores 10 and
  // up, one feature per ten-point score band, from a very light blue to a dark
  // one at 90+. Blue, because
  // OpenStreetMap already uses green and yellow for parks and roads. On a
  // canvas, because it is thousands of rings.
  // No map yet (204) just means no layer.
  private async loadCoverage() {
    const result = await this.service.apiCall('api/wireless_features', {}).toPromise();
    if (!result || !result.features || !this.map) {
      return;
    }
    const scores = result.features.map(feature => feature.properties.min_score);
    const lowest = Math.min(...scores), span = (Math.max(...scores) - lowest) || 1;
    const light = [227, 242, 253], dark = [13, 71, 161];
    const color = (score: number) => `rgb(${light.map((c, i) => Math.round(c + (dark[i] - c) * (score - lowest) / span)).join(',')})`;
    this.map.createPane('coverage').style.zIndex = '350'; // above the tiles, below markers and the beam
    L.geoJSON(result.features, {
      pane: 'coverage',
      renderer: L.canvas({ pane: 'coverage' }),
      interactive: false,
      style: feature => ({ stroke: false, fillColor: color(feature.properties.min_score), fillOpacity: 0.6 })
    } as any).addTo(this.map);
    this.coverage = {
      floors: result.floors,
      date: result.generated_at,
      bands: scores.sort((a, b) => a - b).map(score => ({ score, color: color(score), dark: (score - lowest) / span > 0.5 }))
    };
  }

  private async loadPopSites() {
    const result = await this.service.apiCall('api/pop_site/read', { fields: ['abbr', 'name', 'latitude', 'longitude'], query: ['height', '>', 0], raw: true }).toPromise();
    const sites = Array.isArray(result) ? result : (result && result.data) || [];
    sites.filter(site => +site.latitude && +site.longitude).forEach(site => {
      const label = document.createElement('span'); // a string would be parsed as HTML
      label.textContent = `${site.name} (${site.abbr})`;
      L.circleMarker([+site.latitude, +site.longitude], { radius: 7, color: '#c00', fillOpacity: 0.6 })
        .addTo(this.map)
        .bindTooltip(label);
    });
  }

  onFloorsChange() {
    this.scheduleCheck();
  }

  // One check after the map has settled, instead of one per moveend while the
  // user is still dragging or typing the floor count.
  checkTimer: any;
  private scheduleCheck() {
    clearTimeout(this.checkTimer);
    this.checkTimer = setTimeout(() => this.check(), 400);
  }

  lastCheck = '';
  private async check() {
    if (!this.map) {
      return;
    }
    const center = this.map.getCenter();
    const body = { lat: center.lat, lng: center.lng, floors: this.floors };
    const key = JSON.stringify(body);
    if (key == this.lastCheck) {
      return;
    }
    this.lastCheck = key;
    if (this.floors != null && !(this.floors >= 1 && this.floors <= 60)) {
      this.result = null;
      this.error = this.translator.instant('WIRELESS_FLOORS_REQUIRED');
      this.beam.setLatLngs([]);
      return;
    }
    try {
      const result = await this.service.apiCall('api/wireless_check', body).toPromise();
      if (key != this.lastCheck) {
        return; // the map moved on while this one was in flight
      }
      this.error = null;
      this.result = result;
      if (this.floors == null) {
        this.floors = result.floors;
        this.lastCheck = JSON.stringify({ ...body, floors: this.floors });
      }
      const best = (result.towers || []).find(tower => tower.code === result.best); // no towers in a guest's answer
      this.beam.setLatLngs(best ? [[center.lat, center.lng], [best.lat, best.lon]] : []);
      this.beam.setStyle({ color: result.feasible ? 'green' : 'red' });
    } catch (error) {
      this.result = null;
      this.beam.setLatLngs([]);
      this.error = this.translator.instant('WIRELESS_CHECK_FAILED');
      console.error('Error checking wireless coverage:', error);
    }
  }

  createIssue() {
    const center = this.marker.getLatLng();
    const issueForm = this.activeModal.open(IssueFormComponent, { size: 'lg' });
    issueForm.componentInstance.params = {
      department: 'sales',
      type: this.staff && this.result.feasible ? 'lead-wireless' : 'inquiry-wireless',
      data: {
        latitude: +center.lat.toFixed(6),
        longitude: +center.lng.toFixed(6),
        floors: this.floors,
        pop_site: this.result.best,
        coverage_score: this.result.score
      }
    };
  }

}
